import type { Config } from "tailwindcss";
import tailwindcssAnimate from "tailwindcss-animate";

export default {
	darkMode: ["class"],
	content: [
		"./pages/**/*.{ts,tsx}",
		"./components/**/*.{ts,tsx}",
		"./app/**/*.{ts,tsx}",
		"./src/**/*.{ts,tsx}",
	],
	prefix: "",
	theme: {
		container: {
			center: true,
			padding: '2rem',
			screens: {
				'2xl': '1400px'
			}
		},
		extend: {
			fontFamily: {
				sans: ['Inter Variable', 'Inter', 'system-ui', 'sans-serif'],
				display: ['Outfit Variable', 'Outfit', 'system-ui', 'sans-serif'],
				serif: ['Crimson Pro', 'Georgia', 'serif'],
			},
			colors: {
				border: 'hsl(var(--border))',
				input: 'hsl(var(--input))',
				ring: 'hsl(var(--ring))',
				background: 'hsl(var(--background))',
				foreground: 'hsl(var(--foreground))',
				primary: {
					DEFAULT: 'hsl(var(--primary))',
					foreground: 'hsl(var(--primary-foreground))'
				},
				secondary: {
					DEFAULT: 'hsl(var(--secondary))',
					foreground: 'hsl(var(--secondary-foreground))'
				},
				destructive: {
					DEFAULT: 'hsl(var(--destructive))',
					foreground: 'hsl(var(--destructive-foreground))'
				},
				muted: {
					DEFAULT: 'hsl(var(--muted))',
					foreground: 'hsl(var(--muted-foreground))'
				},
				accent: {
					DEFAULT: 'hsl(var(--accent))',
					foreground: 'hsl(var(--accent-foreground))'
				},
				popover: {
					DEFAULT: 'hsl(var(--popover))',
					foreground: 'hsl(var(--popover-foreground))'
				},
				card: {
					DEFAULT: 'hsl(var(--card))',
					foreground: 'hsl(var(--card-foreground))'
				},
				sidebar: {
					DEFAULT: 'hsl(var(--sidebar-background))',
					foreground: 'hsl(var(--sidebar-foreground))',
					primary: 'hsl(var(--sidebar-primary))',
					'primary-foreground': 'hsl(var(--sidebar-primary-foreground))',
					accent: 'hsl(var(--sidebar-accent))',
					'accent-foreground': 'hsl(var(--sidebar-accent-foreground))',
					border: 'hsl(var(--sidebar-border))',
					ring: 'hsl(var(--sidebar-ring))'
				}
			},
			borderRadius: {
				lg: 'var(--radius)',
				md: 'calc(var(--radius) - 2px)',
				sm: 'calc(var(--radius) - 4px)'
			},
			keyframes: {
				'accordion-down': {
					from: {
						height: '0'
					},
					to: {
						height: 'var(--radix-accordion-content-height)'
					}
				},
				'accordion-up': {
					from: {
						height: 'var(--radix-accordion-content-height)'
					},
					to: {
						height: '0'
					}
				},
				'fade-in': {
					from: {
						opacity: '0',
						transform: 'translateY(20px) scale(0.95)'
					},
					to: {
						opacity: '1',
						transform: 'translateY(0) scale(1)'
					}
				},
				'fade-out': {
					from: {
						opacity: '1',
						transform: 'scale(1)'
					},
					to: {
						opacity: '0',
						transform: 'scale(0.95)'
					}
				},
				'breathe-in': {
					'0%': {
						transform: 'scale(1)',
						opacity: '0.7',
						filter: 'blur(0px)'
					},
					'100%': {
						transform: 'scale(1.5)',
						opacity: '1',
						filter: 'blur(5px)'
					}
				},
				'breathe-hold': {
					'0%, 100%': {
						transform: 'scale(1.5)',
						opacity: '1'
					}
				},
				'breathe-out': {
					'0%': {
						transform: 'scale(1.5)',
						opacity: '1',
						filter: 'blur(5px)'
					},
					'100%': {
						transform: 'scale(1)',
						opacity: '0.7',
						filter: 'blur(0px)'
					}
				},
				'float': {
					'0%, 100%': {
						transform: 'translateY(0px) rotate(0deg)'
					},
					'33%': {
						transform: 'translateY(-20px) rotate(2deg)'
					},
					'66%': {
						transform: 'translateY(-10px) rotate(-2deg)'
					}
				},
				'float-reverse': {
					'0%, 100%': {
						transform: 'translateY(0px) rotate(0deg)'
					},
					'33%': {
						transform: 'translateY(20px) rotate(-2deg)'
					},
					'66%': {
						transform: 'translateY(10px) rotate(2deg)'
					}
				},
				'shimmer': {
					'0%': {
						backgroundPosition: '-1000px 0'
					},
					'100%': {
						backgroundPosition: '1000px 0'
					}
				},
				'gradient-shift': {
					'0%, 100%': {
						backgroundPosition: '0% 50%'
					},
					'50%': {
						backgroundPosition: '100% 50%'
					}
				},
				'pulse-glow': {
					'0%, 100%': {
						boxShadow: '0 0 20px rgba(var(--glow-color), 0.3)'
					},
					'50%': {
						boxShadow: '0 0 40px rgba(var(--glow-color), 0.6), 0 0 60px rgba(var(--glow-color), 0.4)'
					}
				},
				'aurora': {
					'0%': {
						transform: 'translateY(0) translateX(0) scale(1) rotate(0deg)',
						opacity: '0.5'
					},
					'33%': {
						transform: 'translateY(-20px) translateX(20px) scale(1.1) rotate(5deg)',
						opacity: '0.7'
					},
					'66%': {
						transform: 'translateY(-10px) translateX(-20px) scale(0.9) rotate(-5deg)',
						opacity: '0.6'
					},
					'100%': {
						transform: 'translateY(0) translateX(0) scale(1) rotate(0deg)',
						opacity: '0.5'
					}
				},
				'slide-up': {
					from: {
						transform: 'translateY(100%)',
						opacity: '0'
					},
					to: {
						transform: 'translateY(0)',
						opacity: '1'
					}
				},
				'scale-in': {
					from: {
						transform: 'scale(0.8)',
						opacity: '0'
					},
					to: {
						transform: 'scale(1)',
						opacity: '1'
					}
				},
				'particle-float': {
					'0%': {
						transform: 'translateY(0) translateX(0)',
						opacity: '0'
					},
					'10%': {
						opacity: '1'
					},
					'90%': {
						opacity: '1'
					},
					'100%': {
						transform: 'translateY(-100vh) translateX(50px)',
						opacity: '0'
					}
				}
			},
			animation: {
				'accordion-down': 'accordion-down 0.2s ease-out',
				'accordion-up': 'accordion-up 0.2s ease-out',
				'fade-in': 'fade-in 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
				'fade-out': 'fade-out 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
				'breathe-in': 'breathe-in 4s cubic-bezier(0.4, 0, 0.2, 1)',
				'breathe-hold': 'breathe-hold 7s ease-in-out',
				'breathe-out': 'breathe-out 8s cubic-bezier(0.4, 0, 0.2, 1)',
				'float': 'float 6s ease-in-out infinite',
				'float-reverse': 'float-reverse 8s ease-in-out infinite',
				'shimmer': 'shimmer 3s linear infinite',
				'gradient-shift': 'gradient-shift 8s ease infinite',
				'pulse-glow': 'pulse-glow 3s ease-in-out infinite',
				'aurora': 'aurora 15s ease-in-out infinite',
				'slide-up': 'slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
				'scale-in': 'scale-in 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
				'particle-float': 'particle-float 20s linear infinite'
			}
		}
	},
	plugins: [tailwindcssAnimate],
} satisfies Config;
